// Two-cell geometry compare (T30, spec §15 Codex UX idea 4). Read-only — no GIS editing enters the
// app. Opened from the QE/TE cell context menu when the selection spans exactly two spatial cells
// (any two rows/columns, same grid — see the `spatialCompareCandidate` memo in the two grid files).
// One map, geometry A solid / geometry B dashed, auto-fit to the union bbox, plus a compact
// `spatialCompareStats` strip and per-side Copy WKT. Decode runs at the ON-DEMAND cap — see
// `useSpatialCompareDecode`'s header comment for why that differs from the peek's ladder.

import { useEffect, useMemo } from 'react';
import { Copy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { RandomDotmSpinner } from '../../../../components/ui/RandomDotmSpinner';
import { useAnalytics } from '../../../../core/analytics';
import { writeClipboardText } from '../../../../lib/clipboard';
import type { SpatialCellInput } from '../../common/utils/spatialValueAccessor';
import { SPATIAL_LAYER_PALETTE } from '../map/mapThemes';
import { SpatialMapHost, type SpatialMapLayerInput } from '../map/SpatialMapLazy';
import { SpatialCurveApproxHint } from '../map/SpatialCurveApproxHint';
import { formatMeasureDistance } from '../map/useMeasureGesture';
import { loadSpatialDecoder } from '../decode/loadSpatialDecoder';
import { spatialCompareStats } from './spatialCompareStats';
import { useSpatialCompareDecode, type CompareSideState, type SpatialCompareInput } from './useSpatialCompareDecode';

export type SpatialCompareDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  a: SpatialCompareInput;
  b: SpatialCompareInput;
  connectionId?: string;
  database?: string;
};

const DASHED_PATTERN = [4, 3] as const;
const COLOR_A = SPATIAL_LAYER_PALETTE[4]; // blue
const COLOR_B = SPATIAL_LAYER_PALETTE[5]; // vermillion — colorblind-distinct from A

function placeholderLayerDef(id: string, color: string, dasharray?: readonly number[]) {
  return {
    id,
    label: id,
    visible: true,
    source: { kind: 'query' as const, sql: '', geometryRef: { kind: 'column' as const, column: '' } },
    style: { color, lineDasharray: dasharray },
    featureCap: 1,
  };
}

async function copyWkt(side: CompareSideState, input: NonNullable<SpatialCellInput>): Promise<string | null> {
  if (side.status !== 'ready') return null;
  if (input.declaration) {
    const { toWkt } = await loadSpatialDecoder();
    return toWkt(side.geometry.geojson, side.geometry.dimensions);
  }
  const { toWktForExport } = await loadSpatialDecoder();
  return toWktForExport(input.engine, side.value, input.dataType);
}

function CompareSidePanel({
  label, color, dashed, side, onCopyWkt,
}: {
  label: string; color: string; dashed: boolean; side: CompareSideState; onCopyWkt: () => void;
}) {
  return (
    <div className="rounded-md border border-border p-2 text-xs" data-testid={`spatial-compare-side-${label.toLowerCase()}`}>
      <div className="mb-1 flex items-center gap-1.5">
        <span
          className="h-2 w-4 shrink-0 rounded-sm"
          style={{ backgroundColor: color, ...(dashed ? { backgroundImage: `repeating-linear-gradient(90deg, ${color} 0 3px, transparent 3px 5px)`, backgroundColor: 'transparent' } : {}) }}
        />
        <span className="font-medium">Geometry {label}</span>
      </div>
      {side.status === 'loading' && <RandomDotmSpinner size={14} />}
      {side.status === 'ready' && (
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-muted-foreground">{side.summary}</span>
          <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={onCopyWkt} data-testid={`spatial-compare-copy-wkt-${label.toLowerCase()}`}>
            <Copy className="mr-1 h-3 w-3" aria-hidden="true" />
            Copy WKT
          </Button>
        </div>
      )}
      {side.status === 'too-large' && (
        <span className="text-muted-foreground" data-testid={`spatial-compare-too-large-${label.toLowerCase()}`}>
          Too large to view ({side.sizeMb})
        </span>
      )}
      {side.status === 'error' && <span className="text-muted-foreground">{side.message}</span>}
    </div>
  );
}

export function SpatialCompareDialog({ open, onOpenChange, a, b, connectionId, database }: SpatialCompareDialogProps) {
  const { track } = useAnalytics();
  const state = useSpatialCompareDecode({ a, b, enabled: open, connectionId, database });

  useEffect(() => {
    if (open) track('feature:spatial_compare', { engine: a.input.engine });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const layers = useMemo<SpatialMapLayerInput[]>(() => {
    const out: SpatialMapLayerInput[] = [];
    if (state.mapFeatureA) out.push({ def: placeholderLayerDef('compare-a', COLOR_A), features: [state.mapFeatureA] });
    if (state.mapFeatureB) out.push({ def: placeholderLayerDef('compare-b', COLOR_B, DASHED_PATTERN), features: [state.mapFeatureB] });
    return out;
  }, [state.mapFeatureA, state.mapFeatureB]);

  const stats = useMemo(() => {
    if (state.a.status !== 'ready' || state.b.status !== 'ready') return null;
    return spatialCompareStats(state.a.geometry, state.b.geometry, { planar: !!state.planarTransform });
  }, [state.a, state.b, state.planarTransform]);

  // T34a — either compared geometry may be a curve; the shared map shows the linearized shape, but
  // per-side Copy WKT keeps the exact curve. One quiet hint below the map when a curve is present.
  const curveApproximated =
    (state.a.status === 'ready' && state.a.geometry.curved === true) ||
    (state.b.status === 'ready' && state.b.geometry.curved === true);

  async function handleCopy(side: CompareSideState, input: NonNullable<SpatialCellInput>) {
    const text = await copyWkt(side, input);
    if (text == null) return;
    const ok = await writeClipboardText(text);
    if (ok) {
      track('feature:spatial_copy', { format: 'wkt' });
    }
    // Clipboard blocked — do not report a copy that did not happen.
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="spatial-compare-dialog">
        <DialogHeader>
          <DialogTitle>Compare geometries</DialogTitle>
          <DialogDescription>Geometry A (solid) vs geometry B (dashed), fitted to their combined extent.</DialogDescription>
        </DialogHeader>

        <div className="h-64 w-full overflow-hidden rounded-md border border-border" data-testid="spatial-compare-map">
          {layers.length > 0 ? (
            <SpatialMapHost
              layers={layers}
              planarTransform={state.planarTransform}
              projectionNote={state.projectionNote}
              fitToData
              embedded
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <RandomDotmSpinner size={22} />
            </div>
          )}
        </div>

        {curveApproximated && <SpatialCurveApproxHint engine={a.input.engine} />}

        {stats && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-border bg-muted/20 p-2 text-xs" data-testid="spatial-compare-stats">
            <span className="text-muted-foreground">Types</span>
            <span className="font-mono">{stats.geomTypes[0]} vs {stats.geomTypes[1]}</span>
            <span className="text-muted-foreground">Points</span>
            <span className="font-mono">{stats.pointCounts[0]} vs {stats.pointCounts[1]}</span>
            <span className="text-muted-foreground">Bbox overlap (IoU)</span>
            <span className="font-mono">{(stats.bboxIoU * 100).toFixed(1)}%</span>
            <span className="text-muted-foreground">Centroid distance</span>
            <span className="font-mono">
              {stats.centroidDistanceMeters == null ? 'n/a (planar)' : formatMeasureDistance(stats.centroidDistanceMeters)}
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <CompareSidePanel label="A" color={COLOR_A} dashed={false} side={state.a} onCopyWkt={() => void handleCopy(state.a, a.input)} />
          <CompareSidePanel label="B" color={COLOR_B} dashed side={state.b} onCopyWkt={() => void handleCopy(state.b, b.input)} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="spatial-compare-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
